import { computed } from 'vue';
import router from '@/router';
import { loadingController } from '@ionic/vue';
import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';
import * as yup from 'yup';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { Address } from '@/shared/interfaces/wallet';
import { anykrowdApi } from '@/api/anykrowdApi';
import { Cart } from '@/shared/interfaces/cart';
import { EventCheckoutReq } from '@/shared/interfaces/event';
import { GenericProduct } from '@/shared/interfaces/genericProduct';
import { Transaction, TransactionItem } from '@/shared/interfaces/transaction';
import { TransactionType } from '@/enums/wallet.enum';
import { useCart } from '@/modules/shop/composables/useCart';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import useToast from '@/shared/composables/useToast';
import useAlert from '@/shared/composables/useAlert';
import useUrlHelpers from '@/shared/composables/useUrlHelpers';
import useProducts from '@/shared/composables/useProducts';
import useTenant from '@/shared/composables/useTenant';
import { ProductStock, SelfOrderErrorEnum } from '@/enums/values.enum';
import { Currency } from '../interfaces/balance';
import { useAuthStore } from '@/stores/auth/authStore';
import { useSharedStore } from '@/stores/shared/sharedStore';
import { resolveCheckoutErrorMessage } from '@/shared/utils/checkoutError';

// Init
const { buildRedirectUrl } = useUrlHelpers();

export function useCheckout() {
	const { calculateTotalsPerProductCurrency, getItemsAndTotal, getTotalPrice, items, setItemsAndTotal, setAdministrativeFee, setTotalPriceString, totalItemsCount, getTotalPricePerCurrency } = useTransactionStore();
	const itemsComputed = computed(() => items);
	const totalPrice = computed(() => getTotalPrice);
	const totalItemCount = computed(() => totalItemsCount);
	const { getTenantConfigAndStyles } = useTenant();
	const { tenantConfig } = getTenantConfigAndStyles();
	const authStore = useAuthStore();
	const sharedStore = useSharedStore();

	const { getCartItems, getCartSize, cartTotal } = useCart();
	const transactionStore = useTransactionStore();
	const { presentToast } = useToast();
	const { presentAlert } = useAlert();
	const { t } = useI18n();

	// Validation Schema
	const billingAddressValidationSchema = yup.object({
		country: yup.string().required(t('checkout.billing_address.errors.country')),
		city: yup.string().required(t('checkout.billing_address.errors.city')),
		address_street: yup.string().required(t('checkout.billing_address.errors.address_street')),
		address_number: yup.string().required(t('checkout.billing_address.errors.address_number')),
		postal_code: yup.string().required(t('checkout.billing_address.errors.postal_code')),
	});

	const { handleSubmit } = useForm({
		validationSchema: billingAddressValidationSchema,
	});

	// Process checkout
	const processCheckout = async (transactionType: string, additionalInfoForCheckout?: any) => {
		switch (transactionType) {
			case TransactionType.PAYMENT:
				processEventTransaction(additionalInfoForCheckout);
				break;
			case TransactionType.SHOP_ORDER:
				processShopTransaction();
				break;
			case TransactionType.SELF_ORDER:
				processSelfOrderTransaction(additionalInfoForCheckout);
				break;
			case TransactionType.QUEUE_BOOKING:
			case 'QUEUE_BOOKING':
				processQueueBookingTransaction(additionalInfoForCheckout);
				break;
			case 'QUEUE_UPSELL':
				processQueueUpsellTransaction(additionalInfoForCheckout);
				break;
			default:
				// Handle unsupported modules
				break;
		}
		await router.push({ path: '/checkout', query: { transactionType: transactionType } });
	};

	// processSelfOrderTransaction
	const processSelfOrderTransaction = (additionalInfoForProcessSelfOrderTransaction: any) => {
		const items: TransactionItem[] = getCartItems().map((cartItem: Cart) => {
			const product = new GenericProduct(cartItem.product);
			const price = product.discount ? product.discountedPrice()! : cartItem.price;
			return {
				additionalInfo: { ...cartItem.additionalInfo, product },
				itemId: cartItem.product.id,
				description: cartItem.product.name,
				itemPrice: price * cartItem.quantity,
				currency: cartItem.product.currency?.prefix,
				unitPrice: cartItem.price,
				quantity: cartItem.quantity,
				hideQuantity: false,
				discount: cartItem.product.discount,
			};
		});

		// Create transaction
		const transaction: Transaction = {
			additionalInfo: additionalInfoForProcessSelfOrderTransaction.tableSpot.id,
			additionalInfoMore: additionalInfoForProcessSelfOrderTransaction.location,
			items: [...items],
			title: t('checkout.transaction.self_order.title'),
			subtitle: t('checkout.transaction.self_order.subtitle'),
			type: TransactionType.SELF_ORDER,
			totalDescription: `${getCartSize().toString()} ${t('transaction.items_total')}`,
			totalPriceString: cartTotal.value.toString(),
			totalPriceNumber: cartTotal.value,
			totalPriceWithoutFee: cartTotal.value,
		};

		setItemsAndTotal(transaction);

		// To update totalPriceString first we need items set on the store
		// so this is second step needed for events (more than one wallet)
		const totalPriceString = calculateTotalsPerProductCurrency();
		setTotalPriceString(totalPriceString);
	};

	// processShopTransaction
	const processShopTransaction = () => {
		const items: TransactionItem[] = getCartItems().map((cartItem: Cart) => {
			const product = new GenericProduct(cartItem.product);
			const price = product.discount ? product.discountedPrice()! : cartItem.price;
			return {
				itemId: cartItem.product.id,
				description: cartItem.product.name,
				itemPrice: price * cartItem.quantity,
				unitPrice: cartItem.price,
				quantity: cartItem.quantity,
				discount: cartItem.product.discount,
			};
		});

		// Create transaction
		const transaction: Transaction = {
			items: [...items],
			type: TransactionType.SHOP_ORDER,
			totalDescription: `${getCartSize().toString()} ${t('transaction.items_total')}`,
			totalPriceString: cartTotal.value.toString(),
			totalPriceNumber: cartTotal.value,
			totalPriceWithoutFee: cartTotal.value,
		};

		setItemsAndTotal(transaction);
	};

	// processQueueBookingTransaction
	const processQueueBookingTransaction = (additionalInfoForQueueBooking: any) => {
		const items: TransactionItem[] = getCartItems().filter((cartItem: Cart) => cartItem.category === 'queue_ticket').map((cartItem: Cart) => {
			const product = new GenericProduct(cartItem.product);
			const price = cartItem.price;
			return {
				additionalInfo: { ...cartItem.additionalInfo, product },
				itemId: cartItem.product.id,
				description: cartItem.product.name,
				itemPrice: price * cartItem.quantity,
				quantity: cartItem.quantity,
				currency: cartItem.product.currency?.prefix || '€',
			};
		});

		const transaction: Transaction = {
			items: items as TransactionItem[],
			type: TransactionType.QUEUE_BOOKING,
			title: t('queue.purchase_successful_title'),
			subtitle: t('queue.purchase_successful_subtitle'),
			totalDescription: `${getCartSize().toString()} ${t('transaction.queue_passes')}`,
			totalPriceString: cartTotal.value?.toString() || '0',
			totalPriceNumber: cartTotal.value || 0,
			totalPriceWithoutFee: cartTotal.value || 0,
			additionalInfo: {
				...additionalInfoForQueueBooking,
				redirectPath: '/my-queues'
			},
			additionalInfoMore: additionalInfoForQueueBooking,
		};

		setItemsAndTotal(transaction);

		// To update totalPriceString first we need items set on the store
		const totalPriceString = calculateTotalsPerProductCurrency();
		setTotalPriceString(totalPriceString);
	};

	// processQueueUpsellTransaction
	const processQueueUpsellTransaction = (additionalInfoForQueueUpsell: any) => {
		const items: TransactionItem[] = getCartItems().filter((cartItem: Cart) => cartItem.category === 'queue_upsell').map((cartItem: Cart) => {
			const product = new GenericProduct(cartItem.product);
			const price = cartItem.price;
			return {
				additionalInfo: { ...cartItem.additionalInfo, product },
				itemId: cartItem.product.id,
				description: cartItem.product.name,
				itemPrice: price * cartItem.quantity,
				quantity: cartItem.quantity,
				currency: cartItem.product.currency?.prefix || '€',
			};
		});

		const transaction: Transaction = {
			items: items as TransactionItem[],
			type: 'QUEUE_UPSELL' as any,
			title: t('queue.upgrade_successful_title'),
			subtitle: t('queue.upgrade_successful_subtitle'),
			totalDescription: t('queue.upgrade_to_fastpass'),
			totalPriceString: cartTotal.value?.toString() || '0',
			totalPriceNumber: cartTotal.value || 0,
			totalPriceWithoutFee: cartTotal.value || 0,
			additionalInfo: {
				...additionalInfoForQueueUpsell,
				redirectPath: '/my-queues'
			},
			additionalInfoMore: additionalInfoForQueueUpsell,
		};

		setItemsAndTotal(transaction);

		// To update totalPriceString first we need items set on the store
		const totalPriceString = calculateTotalsPerProductCurrency();
		setTotalPriceString(totalPriceString);
	};

	// processEventTransaction
	const processEventTransaction = (additionalInfoForProcessEventTransaction: any) => {
		const items: TransactionItem[] = getCartItems().map((cartItem: Cart) => {
			const product = new GenericProduct(cartItem.product);
			const price = product.discount ? product.discountedPrice()! : cartItem.price;
			return {
				additionalInfo: { ...cartItem.additionalInfo, product },
				itemId: cartItem.product.id,
				description: cartItem.product.name,
				itemPrice: price * cartItem.quantity,
				unitPrice: cartItem.price,
				quantity: cartItem.quantity,
				discount: cartItem.product.discount,
			};
		});

		// Create transaction and save the eventId on additionalInfo
		const transaction: Transaction = {
			additionalInfo: additionalInfoForProcessEventTransaction,
			items: [...items],
			title: t('checkout.transaction.events.title'),
			subtitle: t('checkout.transaction.events.subtitle'),
			type: TransactionType.PAYMENT,
			totalDescription: `${getCartSize().toString()} ${t('transaction.items_total')}`,
			totalPriceString: '',
			totalPriceNumber: cartTotal.value,
			totalPriceWithoutFee: cartTotal.value,
		};

		setItemsAndTotal(transaction);

		// To update totalPriceString first we need items set on the store
		// so this is second step needed for events (more than one wallet)
		const totalPriceString = calculateTotalsPerProductCurrency();
		setTotalPriceString(totalPriceString);
	};

	// Prepare shop order data
	const _prepareShopOrderData = (address: Address) => {
		const products = items.map((item) => {
			return { id: item.itemId, quantity: item.quantity };
		});

		return {
			total_price: Number(totalPrice.value),
			products,
			address,
		};
	};

	// Prepare shop order data
	const _prepareSelfOrderingData = (formData?: any) => {
		const { additionalInfoMore } = getItemsAndTotal();
		const filteredProducts = items.filter((item) => item.additionalInfo.id !== 'administrative_fee');
		const products = filteredProducts.map((item) => {
			return {
				id: item.itemId,
				comments: item.additionalInfo.comments,
				product_category_id: item.additionalInfo.productCategoryId,
				quantity: item.quantity,
			};
		});

		return {
			products,
			disable_payments: items.findIndex((item) => item.additionalInfo.disableSalesCataloguePayments) !== -1,
			table_number: additionalInfoMore,
			self_order_registration_data: formData?.self_order_registration_data ?? undefined,
		};
	};

	const _prepareQueueBookingData = (formData: any) => {
		const { additionalInfoMore } = getItemsAndTotal();
		const filteredItems = items.filter((item) => item.additionalInfo.id !== 'administrative_fee');
		const queuePasses = filteredItems.map((item) => {
			// Get queue_type from additionalInfo or infer from product name/price
			let queueType = item.additionalInfo.queueType || item.additionalInfo.queue_type;

			// If still not found, try to infer from product properties
			if (!queueType && item.additionalInfo?.product) {
				const product = item.additionalInfo.product;
				// Check if it's a priority/fastlane pass based on naming or properties
				if (product.name?.toLowerCase().includes('priority') || product.name?.toLowerCase().includes('fastlane') || product.name?.toLowerCase().includes('fastpass')) {
					queueType = 'priority';
				} else {
					queueType = 'regular';
				}
			}

			// Default to 'regular' if still not set
			if (!queueType) {
				queueType = 'regular';
			}

			return {
				queue_type: queueType,
				activity_id: item.additionalInfo.activityId,
				quantity: item.quantity,
				price: item.itemPrice,
				currency_id: item.additionalInfo?.product?.currency_id
			};
		});

		// Include date and timeslot from additionalInfoMore if available
		const bookingData: any = {
			...formData,
			queue_passes: queuePasses,
		};

		// Check if additionalInfoMore is an object with the properties we need
		if (typeof additionalInfoMore === 'object' && additionalInfoMore !== null) {
			const queueInfo = additionalInfoMore as any;
			if (queueInfo.subActivityId) {
				bookingData.sub_activity_id = queueInfo.subActivityId;
			}
			if (queueInfo.selectedDate) {
				bookingData.selected_date = queueInfo.selectedDate;
			}
			if (queueInfo.selectedTimeSlot) {
				bookingData.selected_time_slot = queueInfo.selectedTimeSlot;
			}
		}

		return bookingData;
	};

	const _prepareQueueUpsellData = (formData: any) => {
		const { additionalInfo } = getItemsAndTotal();

		return {
			...formData,
			slot_id: (additionalInfo as any)?.slotId || additionalInfo,
		};
	};

	const _prepareEventData = (formData: any) => {
		const filteredItems = items.filter((item) => item.additionalInfo.id !== 'administrative_fee');
		const participants = filteredItems.map((item, idx) => {
			return {
				email: item.additionalInfo.email,
				event_combo_ticket_type_id: item.additionalInfo.event_combo_ticket_type_id,
				event_ticket_type_id: item.additionalInfo.event_ticket_type_id,
				name: item.additionalInfo.name,
				row_index: idx,
				user_id: +item.additionalInfo.user_id,
			};
		});

		return {
			...formData,
			participants,
		};
	};

	// Prepare purchase data
	const _preparePuchaseData = async (formData?: any): Promise<{ data: any; redirectUrl: string }> => {
		// Build redirect url
		const redirectUrl = await buildRedirectUrl();

		const { type } = getItemsAndTotal();
		switch (type) {
			case TransactionType.PAYMENT:
				return { data: _prepareEventData(formData), redirectUrl };
			case TransactionType.SHOP_ORDER:
				return { data: _prepareShopOrderData(formData!), redirectUrl };
			case TransactionType.SELF_ORDER:
				return { data: _prepareSelfOrderingData(formData), redirectUrl };
			case TransactionType.QUEUE_BOOKING:
				return { data: _prepareQueueBookingData(formData), redirectUrl };
			case 'QUEUE_UPSELL':
				return { data: _prepareQueueUpsellData(formData), redirectUrl };
			default:
				// Handle unsupported modules
				return { data: {}, redirectUrl: '' };
		}
	};

	const _purchaseShop = async (transactionData: { total_price: number; products: Array<object>; top_up_amount: number | null; redirectUrl: string }): Promise<{ redirect_url: string; code: string }> => {
		const { data } = await anykrowdApi.post<{ redirect_url: string; code: string }>('/shop/purchase', transactionData, true);
		return data;
	};

	const _purchaseQueuePasses = async (purchaseData: any): Promise<any> => {
		try {
			const { data } = await anykrowdApi.post(
				'/queue/purchase',
				purchaseData,
				true
			);
			return {
				code: data.code || 'success',
				redirect_url: data.redirect_url || '',
				topup_amount: data.topup_amount || 0,
				topup_amount_string: data.topup_amount ? `${data.default_currency?.prefix || '€'} ${parseFloat(data.topup_amount).toFixed(2)}` : '',
				order: data.order,
				tickets: data.tickets,
			};
		} catch (error: any) {
			if (error.response?.data) {
				return {
					code: error.response.data.error || 'error',
					message: error.response.data.message || error.response.data.error || t('checkout.purchase_failed'),
					redirect_url: '',
				};
			}
			throw error;
		}
	};

	const _purchaseQueueUpsell = async (purchaseData: any): Promise<any> => {
		try {
			const { data } = await anykrowdApi.post(
				`/queue/slots/${purchaseData.slot_id}/upsell`,
				purchaseData,
				true
			);
			return {
				code: data.code || 'success',
				redirect_url: data.redirect_url || '',
				topup_amount: data.topup_amount || 0,
				topup_amount_string: data.topup_amount ? `${data.default_currency?.prefix || '€'} ${parseFloat(data.topup_amount).toFixed(2)}` : '',
				slot: data.data || data,
			};
		} catch (error: any) {
			if (error.response?.data) {
				return {
					code: error.response.data.error || 'error',
					message: error.response.data.message || error.response.data.error || t('checkout.upgrade_failed'),
					redirect_url: '',
				};
			}
			throw error;
		}
	};

	const _purchaseTickets = async (eventId: number, checkoutData: EventCheckoutReq): Promise<{ success: boolean; redirect_url?: string }> => {
		const { data } = await anykrowdApi.post<{ success: boolean; redirect_url?: string }>(`/events/checkout/${eventId}`, checkoutData, false);
		return data;
	};

	const _purchaseSelfOrder = async (
		tableSpotId: number,
		options: {
			products: { id: number; quantity: number; comments?: string[] }[];
			total: number;
			top_up_amount?: number;
			disable_payments?: boolean;
			redirectUrl?: string;
			redirect_url?: string;
			table_number?: string;
			self_order_registration_data?: Record<string, string>;
		},
	): Promise<{
		message?: string;
		code?: string;
		success?: string;
		redirect_url?: string;
		transactions?: any[];
		topup_amount?: number;
		currency?: Currency;
		default_currency?: Currency;
	}> => {
		try {
			const { data } = await anykrowdApi.post<{ status: string; message: string; redirect_url?: string }>(`/table-spot/${tableSpotId}/order`, options, false);
			return data;
		} catch (e: any) {
			const responseErrorCode = e.response?.data?.error || e.response?.data?.code;
			const responseMessage = e.response?.data?.message || e.response?.data?.error || e.message;

			if (e.response?.data?.error === SelfOrderErrorEnum.INSUFFICIENT_BALANCE) {
				return {
					message: e.response?.data?.message,
					code: e.response?.data?.error,
					redirect_url: '',
					transactions: [],
				};
			} else {
				return {
					message: responseMessage,
					code: responseErrorCode || e.code,
					redirect_url: '',
					transactions: [],
				};
			}
		}
	};

	const _rewriteTransactions = (transactions: any[] | undefined, selfOrderData: any) => {
		// so the key is the currencyId
		// value is the amount burned for that currency
		if (transactions) {
			const mappedTransactions: any = [];
			transactions.forEach((transaction, index) => {
				// const wallet = wallets.value?.find((wallet) => wallet.id === transaction.wallet_id);
				const mappedTransaction: TransactionItem = {
					additionalInfo: {
						product: {
							currency: {
								name: transaction.currency.name,
								prefix: transaction.currency.prefix,
							},
						},
					},
					itemId: index,
					description: transaction.name,
					unitPrice: transaction.sold_price,
					quantity: transaction.quantity,
					hideQuantity: false,
					hidePrice: false,
					itemPrice: transaction.total_price,
					currency: transaction.currency.prefix,
				};
				mappedTransactions.push(mappedTransaction);
			});

			const currencies = new Set(mappedTransactions.map((transaction: TransactionItem) => transaction.currency));
			const totalPriceString = currencies.size === 1 ? `${mappedTransactions[0].currency} ${mappedTransactions.reduce((sum: number, t: TransactionItem) => sum + t.itemPrice, 0).toFixed(2)}` : '';
			const showTotalPriceString = totalPriceString === '' ? false : true;

			if (selfOrderData.code === 'waiting_topup') {
				setItemsAndTotal({
					...getItemsAndTotal(),
					items: mappedTransactions,
					type: TransactionType.SELF_ORDER,
					totalPriceString: totalPriceString,
					showTotalPriceString: showTotalPriceString,
				});

				setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));
				const totalItemsPriceString = transactionStore.calculateTotalsPerProductCurrency();
				transactionStore.totalPriceString = totalItemsPriceString;
			}
		}
	};

	const _executePurchase = async (purchaseData: any) => {
		const { additionalInfo, type } = getItemsAndTotal();

		// Save purchase data
		sharedStore.purchaseData = {
			additionalInfo: +additionalInfo,
			purchaseData,
		};

		switch (type) {
			case TransactionType.PAYMENT: {
				const ticketsData = await _purchaseTickets(+additionalInfo, purchaseData);
				return { redirect_url: ticketsData.redirect_url, code: ticketsData.success };
			}
			case TransactionType.SHOP_ORDER: {
				const shopData = await _purchaseShop(purchaseData);
				return { redirect_url: shopData.redirect_url, code: shopData.code };
			}
			case TransactionType.SELF_ORDER: {
				const selfOrderData = await _purchaseSelfOrder(+additionalInfo, purchaseData);
				const transactions = selfOrderData.transactions;

				// Rewrite transactions to show the actual burned tokens
				_rewriteTransactions(transactions, selfOrderData);

				const currency = selfOrderData.currency ? selfOrderData.currency.prefix : selfOrderData.default_currency ? selfOrderData.default_currency.prefix : '€';

				return {
					code: selfOrderData.code,
					redirect_url: selfOrderData.redirect_url,
					topup_amount: selfOrderData.topup_amount,
					topup_amount_string: `${currency} ${selfOrderData.topup_amount?.toFixed(2).toString()}`,
					message: selfOrderData.message,
				};
			}
			case TransactionType.QUEUE_BOOKING: {
				// Call the queue purchase API
				const queueData = await _purchaseQueuePasses(purchaseData);
				return {
					code: queueData.code || 'success',
					redirect_url: queueData.redirect_url || '',
					topup_amount: queueData.topup_amount || 0,
					topup_amount_string: queueData.topup_amount_string || '',
					message: queueData.message || ''
				};
			}
			case 'QUEUE_UPSELL': {
				// Call the queue upsell API
				const upsellData = await _purchaseQueueUpsell(purchaseData);
				return {
					code: upsellData.code || 'success',
					redirect_url: upsellData.redirect_url || '',
					topup_amount: upsellData.topup_amount || 0,
					topup_amount_string: upsellData.topup_amount_string || '',
					message: upsellData.message || ''
				};
			}
			default:
				// Handle unsupported modules
				return { redirect_url: '', code: '', error: '' };
		}
	};

	const _openPaymentRedirect = async (redirectUrl: string) => {
		if (Capacitor.isNativePlatform()) {
			try {
				await AppLauncher.openUrl({ url: redirectUrl });
				return;
			} catch (e) {
				console.log(e);
			}
		}

		window.location.href = redirectUrl;
	};

	const _routeFromRedirectUrl = (redirectUrl: string): { path: string; query: Record<string, string> } => {
		const url = new URL(redirectUrl);

		if (url.hash) {
			const hashPath = url.hash.substring(1);
			const [path, queryString = ''] = hashPath.split('?');

			return {
				path,
				query: Object.fromEntries(new URLSearchParams(queryString)),
			};
		}

		return {
			path: url.pathname,
			query: Object.fromEntries(url.searchParams),
		};
	};

	/**
	 * Check if the products are still available in stock
	 */
	const _checkSelfOrderStock = async () => {
		let inStock = true;
		const { getProductPrices } = useProducts();

		const productsInCart = items.map((item) => {
			return {
				id: item.itemId,
				comments: item.additionalInfo.comments,
				product_category_id: item.additionalInfo.productCategoryId,
				quantity: item.quantity,
			};
		});

		const products = await getProductPrices({
			ids: productsInCart.map((p) => p.id).join(','),
		});

		products.data.forEach((product) => {
			const productInCart = productsInCart.find((p) => p.id === product.id);
			if ((product.stock_type != ProductStock.infinity, productInCart && Math.round(Number(product.stock_relation) * 100) < Math.round(Number(productInCart.quantity) * 100))) {
				inStock = false;
			}
		});

		return inStock;
	};

	// _checkStock
	const _checkStock = async () => {
		const { type } = getItemsAndTotal();
		switch (type) {
			case TransactionType.SELF_ORDER:
				return await _checkSelfOrderStock();
			default:
				return true;
		}
	};

	// handleCheckoutWithForm
	const handleCheckoutWithForm = handleSubmit(async (formData: any) => {
		handleCheckout(false, formData);
	});

	// handleCheckout
	const handleCheckout = async (checkStock = false, formData?: any) => {
		// Show loader
		const loading = await loadingController.create({ message: t('general.please_wait'), backdropDismiss: true });
		await loading.present();

		// Check stock if requested
		const inStock = checkStock ? await _checkStock() : true;
		if (!tenantConfig.value!.out_of_stock_order_clientx && !inStock) {
			await loading.dismiss();
			await presentToast('top', t('checkout.errors.stock_alert'), 5000, 'danger');
		} else {
			// Prepare purchase data
			const { data, redirectUrl } = await _preparePuchaseData(formData);

			const badConnectionTimeout = setTimeout(async () => {
				// if we reach this, we don't have a response from the server yet
				await loading.dismiss();
				// await (
				// 	await alertController.create({
				// 		header: t('checkout.errors.connection.header'),
				// 		'message': t('checkout.errors.connection.message'),
				// 		buttons: ['OK'],
				// 	})
				// ).present();
			}, 15000);

			// Execute call
			try {
				const { redirect_url, code, topup_amount, topup_amount_string, message } = await _executePurchase({ ...data, redirect_url: redirectUrl, redirectUrl });
				clearTimeout(badConnectionTimeout);
				if ((code && code === 'purchase_completed_successfully') || code === 'purchase_success'
					|| code === 'success') {
					await loading.dismiss();

					const targetRoute = _routeFromRedirectUrl(redirect_url || redirectUrl);

					if (targetRoute.path) {
						// Push to the route
						await router.push({
							path: targetRoute.path,
							query: {
								...targetRoute.query,
								fromMollie: 'false',
							},
						});
					}
				} else if (code === 'waiting_topup') {
					// User has to top up before continuing
					await loading.dismiss();

					if (!authStore.isAuthenticated() && redirect_url) {
						await _openPaymentRedirect(redirect_url);
						return;
					}

					let message = t('top_up.insufficient_balance_message', {
						amount: `${topup_amount_string}`,
						administrative_fee: `€${tenantConfig.value!.top_up_cost}`,
					});
					message = topup_amount! >= 1 ? message : `${message} ${t('top_up.minimum_top_up_amount')}`;

					await presentAlert({
						header: t('top_up.insufficient_balance'),
						message: message,
						buttons: [
							{
								text: t('general.cancel'),
								role: 'cancel',
							},
							{
								text: t('general.confirm'),
								role: 'confirm',
								handler: async () => {
									await _openPaymentRedirect(redirect_url!);
								},
							},
						],
					});
				} else if ((redirect_url && code !== 'purchase_completed_successfully') || (redirect_url && code === 'guest_payment')) {
					await loading.dismiss();

					await _openPaymentRedirect(redirect_url);
				} else if (code === SelfOrderErrorEnum.INSUFFICIENT_BALANCE) {
					await loading.dismiss();
					await presentToast('top', t('self_ordering.errors.insufficient_balance'), 5000, 'danger');
				} else if (code === 'mixed_charge_mode_not_supported') {
					await loading.dismiss();
					await presentToast('top', t('checkout.errors.mixed_charge_modes'), 5000, 'danger');
				} else if (
					code === 'booking_failed' ||
					code === 'error' ||
					code === 'BOOKING_CONSTRAINT_FAILED' ||
					code === 'QUEUE_NOT_AVAILABLE' ||
					code === 'MAX_BOOKINGS_EXCEEDED' ||
					code === 'PRICE_NOT_CONFIGURED' ||
					code === 'INVALID_QUEUE_TYPE' ||
					code === 'SLOT_NOT_ACTIVE' ||
					code === 'UPSELL_NOT_AVAILABLE' ||
					code === 'INVALID_TOPUP_AMOUNT' ||
					code === 'INSUFFICIENT_CAPACITY' ||
					code === 'FASTLANE_SOLD_OUT'
				) {
					// Handle queue booking errors and other general errors
					await loading.dismiss();
					const errorMessage = message || t('common.error');
					await presentToast(
						'top',
						errorMessage,
						5000,
						'danger'
					);
				} else {
					await loading.dismiss();
				}
			} catch (error: any) {
				clearTimeout(badConnectionTimeout);
				await loading.dismiss();
				await presentToast('top', resolveCheckoutErrorMessage(error, t), 5000, 'danger');
			}
		}
	};

	return {
		items: itemsComputed.value,
		totalItemsPrice: totalPrice.value,
		totalItemsPriceString: calculateTotalsPerProductCurrency(),
		totalItemCount: totalItemCount.value,
		getTotalPricePerCurrency,
		processCheckout,
		handleCheckout,
		handleCheckoutWithForm,
		billingAddressValidationSchema,
	};
}
